import { useCallback, useRef } from 'react';

/**
 * useLauncherOrigin — bind a ref to a launcher element (icon or button)
 * and get a stable `getRect()` that reads its current bounding client rect
 * at call time.
 *
 * Pass `getRect` through to consumers so the origin is always live —
 * scroll, layout shifts, and viewport resize are handled automatically
 * because the rect is re-read every time it's needed (not cached at open).
 *
 * @example
 * const { ref, getRect } = useLauncherOrigin();
 * // spread ref on the launcher's outer element:
 * <div ref={ref} onClick={handleClick}>...</div>
 * // pass getRect to consumers that need the origin at animation time:
 * service.openIframeModalFromLauncher(..., { getOriginRect: getRect });
 */
export const useLauncherOrigin = () => {
  const ref = useRef(null);

  const getRect = useCallback(() => {
    return ref.current?.getBoundingClientRect() || null;
  }, []);

  // eslint-disable-next-line sort-keys
  return { ref, getRect };
};
